import React from 'react';
import BBALoader from '../BBALoader';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import { statusStyle, sourceBadge } from './financeBenchmarks';
import MetricDebugInfo from '../dev/MetricDebugInfo';

/**
 * Reusable live finance mini-dashboard widget.
 *
 * Renders a header (icon + title + source badge), a headline value with a
 * benchmark-colored accent, an optional coaching cue, an optional chart slot
 * (children), and a "View full report" link to the metric's full page.
 *
 * Props:
 *  - title, icon
 *  - value (string|node), valueStatus ('good'|'ok'|'poor'|'none')
 *  - sub (string|node) small line under the value
 *  - source ('quickbooks'|'jobtread'|'manual'|'none')
 *  - cue (string) coaching cue
 *  - fullPagePath (string) route for the "View full report" link
 *  - loading (bool)
 *  - empty (bool) + emptyText / emptyActionLabel
 *  - emptyAction (node) custom button rendered under emptyText (e.g. open settings)
 *  - compact (bool) tighter padding/sizing
 *  - children: chart / extra content
 */
const MetricWidget = ({
  title,
  titleMeta,
  icon,
  value,
  valueStatus = 'none',
  sub,
  source = 'none',
  cue,
  fullPagePath,
  fullPageLabel = 'View full report',
  loading = false,
  empty = false,
  emptyText = 'No data yet',
  emptyActionLabel = 'Open',
  emptyAction,
  compact = false,
  headerAction,
  showSourceBadge = true,
  showStatusPill = true,
  trace = null,
  stretchChart = false,
  footer,
  children
}) => {
  const sStyle = statusStyle(valueStatus);
  const badge = sourceBadge(source);

  return (
    <div className={`flex flex-col rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow ${compact ? 'p-4' : 'p-5'} ${stretchChart ? 'h-full' : ''}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {icon && <span className="text-red-600 text-lg shrink-0">{icon}</span>}
          <h3 className="font-semibold text-gray-800 dark:text-white truncate">{title}</h3>
          {titleMeta && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{titleMeta}</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {headerAction}
          {showSourceBadge && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.classes}`}>
              {badge.label}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center min-h-[96px]">
          <BBALoader size={28} />
        </div>
      ) : empty ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center min-h-[96px] py-2">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{emptyText}</p>
          {emptyAction}
          {fullPagePath && (
            <Link to={fullPagePath} className="text-sm font-medium text-red-600 hover:text-red-700 hover:underline">
              {emptyActionLabel}
            </Link>
          )}
          {/* Dev-mode trace explains why this widget is empty */}
          <div className="w-full text-left">
            <MetricDebugInfo trace={trace} />
          </div>
        </div>
      ) : (
        <>
          {/* Value (skipped when null so chart-only widgets like the gauge can lead) */}
          {value != null && value !== '' && (
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className={`${compact ? 'text-2xl' : 'text-3xl'} font-bold ${sStyle.text}`}>{value}</span>
              {showStatusPill && valueStatus !== 'none' && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${sStyle.bg} ${sStyle.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${sStyle.dot}`} />
                  {sStyle.label}
                </span>
              )}
            </div>
          )}
          {sub && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</div>}

          {/* Chart slot (flexes to fill the card when stretchChart is on) */}
          {children && <div className={`mt-3 ${stretchChart ? 'flex-1 min-h-0 flex flex-col' : ''}`}>{children}</div>}

          {/* Coaching cue */}
          {cue && <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">{cue}</p>}

          {/* Dev-mode trace (only passed in when dev mode is on) */}
          <MetricDebugInfo trace={trace} />
        </>
      )}

      {/* Centered footer note (pushed to the card bottom when it stretches) */}
      {footer && !empty && (
        <div className="mt-auto pt-3 border-t border-gray-100 dark:border-gray-700 text-center text-xs text-gray-500 dark:text-gray-400">
          {footer}
        </div>
      )}

      {/* Footer link */}
      {fullPagePath && !empty && (
        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
          <Link
            to={fullPagePath}
            className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700 hover:underline"
          >
            {fullPageLabel}
            <FaArrowRight className="text-xs" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default MetricWidget;
